import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClockRotateLeft, faArrowRight} from "@fortawesome/free-solid-svg-icons";

// Context
import {AuthContext} from "../../contexts/auth.js";
import {NavContext} from "../../contexts/nav.js";
import {useSocket} from "../../contexts/socket.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {toTitleCase} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import ModalFacilityStateHistory from "./ModalFacilityStateHistory.js";
import {
  InputCheck,
  InputCheckGroup,
  InputRadioGroup,
} from "../../components/form/FormInputs.js";
import InputCalendar, { calendarValidation } from "../../components/form/InputCalendar.js";
import Help from "../../components/Help.js";

// Style
import {border, pad, radius, shadow} from "../../style/components/variables.js";
import {pulse} from "../../style/components/animations.js";
import {
  Abbr,
  Button,
  Form,
  FormField,
  Heading,
  HeadingMedium,
  Inline,
  List,
  ListItem,
  ModalButton,
  Pill,
  RelativeWrapper,
  Small,
  Span,
  Text,
  ButtonLoader,
  Warning
} from "../../style/components/general.js";

// Socket Constants
import {
  CHECK_STATE,
  STATE_ON,
  STATE_OFF,
  SET_STATE_ON,
  SET_STATE_OFF
} from "./Room.js";

const fieldKeyForChecksheet = (facilityId, checksheetStateId) =>
  `checksheet_${facilityId}_${checksheetStateId}`;

const flattenGroupChecksheets = groups =>
  groups.flatMap(({target, checksheets}) =>
    checksheets.map(cs => ({...cs, facilityId: target.facilityId}))
  );

const buildDueDatesSchema = groups =>
  yup
    .object()
    .shape(
      Object.fromEntries(
        flattenGroupChecksheets(groups).map(({facilityId, checksheetStateId}) => [
          fieldKeyForChecksheet(facilityId, checksheetStateId),
          calendarValidation
        ])
      )
    );

const buildDueDatesDefaults = groups =>
  flattenGroupChecksheets(groups).reduce(
    (acc, {facilityId, checksheetStateId, defaultStartDate}) => {
      acc[fieldKeyForChecksheet(facilityId, checksheetStateId)] = {
        date: defaultStartDate,
        visibleDate: dayjs(defaultStartDate).format("MM/DD/YYYY"),
        lastDay: false
      };
      return acc;
    },
    {}
  );

const TypeStateIndicators = ({slug, statesBySlug, allStates}) => {
  const typeStates = (statesBySlug[slug] || []).filter(state => allStates?.[state]);
  if (!typeStates.length) return null;
  return (
    <Inline>
      {typeStates.map(state => (
        <StateCircle key={state} color={`#${allStates[state].color}`} title={`ON ${state}`}>
          <StateCircleChar>{state.charAt(0).toUpperCase()}</StateCircleChar>
        </StateCircle>
      ))}
    </Inline>
  );
};

TypeStateIndicators.propTypes = {
  slug: PropTypes.string.isRequired,
  statesBySlug: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  allStates: PropTypes.objectOf(PropTypes.any)
};

const DueDatesStep = ({groups, stateEnabling, onSubmit, statesBySlug, allStates}) => {
  const dueDatesForm = useForm({
    resolver: yupResolver(buildDueDatesSchema(groups)),
    defaultValues: buildDueDatesDefaults(groups)
  });

  const multiType = groups.length > 1;

  return (
    <FormProvider {...dueDatesForm}>
      <Form onSubmit={dueDatesForm.handleSubmit(onSubmit)} noValidate>
        <Text>
          {multiType
            ? "The following checksheets have schedule changes across the selected types. Confirm the next due date for each."
            : "The following checksheets have schedule changes. Confirm the next due date for each."}
        </Text>
        {groups.map(({target, checksheets}) => (
          <FormField key={target.slug || target.facilityId}>
            {multiType && (
              <Inline>
                <HeadingMedium>{(target.typeName || "Type").toUpperCase()}</HeadingMedium>
                <TypeStateIndicators
                  slug={target.slug}
                  statesBySlug={statesBySlug}
                  allStates={allStates}
                />
              </Inline>
            )}
            {checksheets.map(({checksheetStateId, name, frequency, currentFrequency}) => (
              <FormField key={`${target.facilityId}-${checksheetStateId}`}>
                <Inline wrap>
                  <HeadingMedium>{name}</HeadingMedium>
                  <Inline>
                    <Pill>{currentFrequency}</Pill>
                    <FontAwesomeIcon icon={faArrowRight} />
                    <Pill>{frequency}</Pill>
                  </Inline>
                </Inline>
                <InputCalendar
                  name={fieldKeyForChecksheet(target.facilityId, checksheetStateId)}
                  label="NEXT DUE DATE"
                />
              </FormField>
            ))}
          </FormField>
        ))}
        <Button type="submit">Confirm and Toggle {stateEnabling ? "ON" : "OFF"}</Button>
      </Form>
    </FormProvider>
  );
};

DueDatesStep.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  stateEnabling: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  statesBySlug: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  allStates: PropTypes.objectOf(PropTypes.any)
};

const ModalFacilityState = ({visible, setVisible, facility}) => {
  const isMounted = useMountedState();

  const socket = useSocket();

  const {
    currentUser
    // roleCanAccessResource
  } = useContext(AuthContext);
  const {allStates} = useContext(NavContext);
  // const {settings} = useContext(SettingsContext);

  const {api: apiNotification} = useApi("notifications");
  const {api: apiPreview} = useApi("facility-state-change-preview", {
    suppress: {success: true, error: false}
  });

  const [notifications, setNotifications] = useState();
  const [activeStates, setActiveStates] = useState();
  const [statesBySlug, setStatesBySlug] = useState({});
  const [view, setView] = useState("manage");
  const [dueDateGroups, setDueDateGroups] = useState([]);
  const [confirmTargets, setConfirmTargets] = useState([]);
  const [stateEnabling, setStateEnabling] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      state: undefined,
      applyToOtherTypes: false,
      applyTypes: []
    },
    resolver: yupResolver(
      yup.object().shape({
        state: yup.string().required("Target state has not been selected.")
      })
    )
  });
  const {watch, handleSubmit, setValue, setError, clearErrors} = form;
  const watchState = watch("state");
  const watchApplyToOtherTypes = watch("applyToOtherTypes");

  useEffect(() => {
    if (watchState) clearErrors("state");
  }, [watchState, clearErrors]);

  const typeRows = useMemo(() => {
    if (facility?.types?.length) return facility.types;
    return [
      {
        facilityId: facility.id,
        slug: facility.slug,
        name: facility.type || "Current"
      }
    ];
  }, [facility]);

  const typeSlugs = useMemo(() => typeRows.map(t => t.slug).filter(Boolean), [typeRows]);

  const siblingTypes = useMemo(() => {
    if (!facility?.types?.length) return [];
    return facility.types.filter(t => t.facilityId !== facility.id && t.slug !== facility.slug);
  }, [facility]);

  const siblingTypeOptions = useMemo(
    () =>
      siblingTypes.map(t => ({
        name: `${t.facilityId}`,
        label: (
          <Inline>
            <span>{t.name?.toUpperCase() || t.slug}</span>
            <TypeStateIndicators slug={t.slug} statesBySlug={statesBySlug} allStates={allStates} />
          </Inline>
        )
      })),
    [siblingTypes, statesBySlug, allStates]
  );

  const facilityStateOn = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => {
        const existing = prev[payload.facility] || [];
        if (existing.includes(payload.state)) return prev;
        return {...prev, [payload.facility]: [...existing, payload.state]};
      });
      if (payload.setBy && payload.facility === facility.slug) {
        setActiveStates(prev => ({...prev, [payload.state]: payload.setBy}));
      }
    },
    [typeSlugs, facility.slug]
  );

  const facilityStateOff = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => ({
        ...prev,
        [payload.facility]: (prev[payload.facility] || []).filter(state => state !== payload.state)
      }));
      if (payload.facility === facility.slug) {
        setActiveStates(prev => {
          if (!prev?.[payload.state]) return prev;
          const next = {...prev};
          delete next[payload.state];
          return next;
        });
      }
    },
    [typeSlugs, facility.slug]
  );

  // Clear stale active states when switching facility type, then re-check Redis.
  useEffect(() => {
    setActiveStates({});
    setStatesBySlug({});
  }, [facility.slug]);

  // Socket Management
  useEffect(() => {
    if (isMounted() && currentUser && currentUser.publicId) {
      typeSlugs.forEach(typeSlug => socket.emit(CHECK_STATE, typeSlug));
    }

    socket.on(STATE_ON, facilityStateOn);
    socket.on(STATE_OFF, facilityStateOff);

    return () => {
      socket.off(STATE_ON, facilityStateOn);
      socket.off(STATE_OFF, facilityStateOff);
    };
  }, [socket, isMounted, currentUser, typeSlugs, facilityStateOn, facilityStateOff]);

  useEffect(() => {
    if (!watchApplyToOtherTypes) setValue("applyTypes", []);
  }, [watchApplyToOtherTypes, setValue]);

  // Get notifications if state is defined
  useEffect(() => {
    if (isMounted() && facility.id && watchState)
      apiNotification
        .callGet(null, {
          facilityId: facility.id,
          notificationName: `State${toTitleCase(watchState)}`
        })
        .then(({status, data}) => {
          if (status === 200 && data?.length > 0) setNotifications(data);
        });
  }, [isMounted, facility.id, watchState, apiNotification]);

  const finishQueue = useCallback(() => {
    setVisible(false);
    setView("manage");
    setDueDateGroups([]);
    setConfirmTargets([]);
    setPreviewLoading(false);
  }, [setVisible]);

  const emitForTarget = useCallback(
    (target, startDates, enabling) => {
      socket.emit(
        enabling ? SET_STATE_ON : SET_STATE_OFF,
        target.slug,
        currentUser.publicId,
        allStates[watchState].id,
        startDates || null
      );
    },
    [socket, currentUser.publicId, allStates, watchState]
  );

  const processTargets = useCallback(
    (targets, enabling) => {
      if (!targets?.length) {
        finishQueue();
        return;
      }

      setPreviewLoading(true);
      setConfirmTargets(targets);

      Promise.all(
        targets.map(target =>
          apiPreview
            .callGet(null, {
              facilityId: target.facilityId,
              stateId: allStates[watchState].id,
              enabled: enabling
            })
            .then(response => {
              const checksheets = response?.status === 200 ? response?.data?.checksheets || [] : [];
              return {target, checksheets};
            })
            .catch(() => ({target, checksheets: []}))
        )
      ).then(results => {
        if (!isMounted()) return;
        const groups = results.filter(({checksheets}) => checksheets.length > 0);
        if (groups.length > 0) {
          setDueDateGroups(groups);
          setView("dueDates");
          setPreviewLoading(false);
        } else {
          results.forEach(({target}) => emitForTarget(target, null, enabling));
          finishQueue();
        }
      });
    },
    [apiPreview, allStates, watchState, emitForTarget, finishQueue, isMounted]
  );

  const handleUpdate = values => {
    const enabling = !(activeStates && activeStates[watchState]);
    setStateEnabling(enabling);

    const targets = [
      {
        facilityId: facility.id,
        slug: facility.slug,
        typeName: facility.type || "Current"
      }
    ];

    if (values.applyToOtherTypes && values.applyTypes?.length) {
      const selectedIds = new Set(values.applyTypes.map(String));
      siblingTypes.forEach(t => {
        if (selectedIds.has(String(t.facilityId))) {
          targets.push({
            facilityId: t.facilityId,
            slug: t.slug,
            typeName: t.name
          });
        }
      });
    }

    processTargets(targets, enabling);
  };

  const handleDueDatesSubmit = values => {
    confirmTargets.forEach(target => {
      const group = dueDateGroups.find(g => g.target.facilityId === target.facilityId);
      let startDates = null;
      if (group?.checksheets?.length) {
        startDates = group.checksheets.reduce((acc, {checksheetStateId}) => {
          const field = values[fieldKeyForChecksheet(target.facilityId, checksheetStateId)];
          if (field?.date) acc[`${checksheetStateId}`] = field.date;
          return acc;
        }, {});
        if (!Object.keys(startDates).length) startDates = null;
      }
      emitForTarget(target, startDates, stateEnabling);
    });
    finishQueue();
  };

  const togglingOn = !(activeStates && activeStates[watchState]);

  if (view === "history") {
    return (
      <ModalFacilityStateHistory
        visible={visible}
        setVisible={setVisible}
        goBack={() => setView("manage")}
        facility={facility}
      />
    );
  }

  if (view === "dueDates") {
    return (
      <Modal
        visible={visible}
        setVisible={setVisible}
        hasBackButton
        goBack={() => {
          setView("manage");
          setDueDateGroups([]);
          setConfirmTargets([]);
          setPreviewLoading(false);
        }}>
        <ModalHeading>Set Due Dates</ModalHeading>
        <DueDatesStep
          groups={dueDateGroups}
          stateEnabling={stateEnabling}
          statesBySlug={statesBySlug}
          allStates={allStates}
          onSubmit={handleDueDatesSubmit}
        />
      </Modal>
    );
  }

  if (!allStates) return null;

  const facilityName = facility?.name?.toUpperCase() || "";
  const stateKeys = Object.keys(allStates);
  const activeStateRows = typeRows.map(typeRow => {
    const activeForType = new Set((statesBySlug[typeRow.slug] || []).filter(s => allStates?.[s]));

    return {
      key: typeRow.slug || `${typeRow.facilityId}`,
      facilityName,
      facilityType: typeRow.name || "",
      states: stateKeys.map(state => ({
        key: state,
        enabled: activeForType.has(state),
        stateLabel: state.toUpperCase(),
        color: allStates[state].color ? `#${allStates[state].color}` : undefined
      }))
    };
  });

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      renderModalControls={
        <ModalButton type="button" onClick={() => setView("history")} title="State History">
          <FontAwesomeIcon icon={faClockRotateLeft} />
        </ModalButton>
      }>
      <ModalHeading>
        Manage State&nbsp;
        <Help>
          Facility states describe this site&apos;s operating context (for example a season, zone,
          or mode). Turning a state on uses any matching checksheet versions—their fields, rules,
          and schedule—and may change due dates. A state can also show a badge and send
          notifications.
        </Help>
      </ModalHeading>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleUpdate)} noValidate>
          <FormField>
            <ActiveStateCard>
              <Small bold>ACTIVE STATE{activeStateRows.length === 1 ? "" : "S"}</Small>
              <List column>
                {activeStateRows.map(row => (
                  <ListItem key={row.key} inline>
                    <Inline>
                      <Span>{row.facilityName}</Span>
                      {row.facilityType && <Span>{row.facilityType}</Span>}
                      &middot;
                      {row.states.map(state => (
                        <React.Fragment key={state.key}>
                          <Text bold color={state.enabled ? state.color : undefined}>
                            {state.enabled ? "ON" : "OFF"}
                          </Text>
                          <Text bold color={state.enabled ? state.color : undefined}>
                            {state.stateLabel}
                          </Text>
                          {state.enabled && (
                            <StateCircle color={state.color} title={`ON ${state.stateLabel}`}>
                              <StateCircleChar>
                                {state.stateLabel.charAt(0).toUpperCase()}
                              </StateCircleChar>
                            </StateCircle>
                          )}
                        </React.Fragment>
                      ))}
                    </Inline>
                  </ListItem>
                ))}
              </List>
            </ActiveStateCard>
          </FormField>

          <FormField>
            <InputRadioGroup
              name="state"
              label="Target State"
              required
              options={Object.keys(allStates).map(s => ({
                label: s,
                id: allStates[s].name,
                labelColor: allStates[s].color ? `#${allStates[s].color}` : undefined
              }))}
            />
          </FormField>
          {allStates[watchState]?.description && (
            <Text>{allStates[watchState].description}</Text>
          )}
          {notifications && (
            <RelativeWrapper>
              <Text bold>Configured to Notify:</Text>
              <Email>
                {notifications.map(email => (
                  <Abbr title={email} key={email}>
                    {email}
                  </Abbr>
                ))}
              </Email>
            </RelativeWrapper>
          )}
          {watchState && siblingTypes.length > 0 && (
            <ApplyOtherTypes>
              <InputCheck name="applyToOtherTypes">
                Apply to additional types{" "}
                <Help>
                  The currently targetted type is {facility.type}. Toggling additional facility
                  types will apply the {togglingOn ? "ON" : "OFF"} state to all selected regardless
                  of current type.
                </Help>
              </InputCheck>
              {watchApplyToOtherTypes && (
                <OtherTypesPanel>
                  <InputCheckGroup name="applyTypes" options={siblingTypeOptions} all />
                </OtherTypesPanel>
              )}
            </ApplyOtherTypes>
          )}
          {watchApplyToOtherTypes && (
            <Small>
              <Warning>Warning!</Warning>Toggling all facility types {togglingOn ? "ON" : "OFF"} will set the selected
              state regardless of the active state currently set.
            </Small>
          )}
          <br />
          <ToggleWrap
            onClick={() => {
              if (!watchState) {
                setError("state", {
                  type: "manual",
                  message: "Please select a target state."
                });
              }
            }}>
            <Button
              type="submit"
              loading={previewLoading ? 1 : 0}
              disabled={!watchState || previewLoading}>
              {previewLoading && <ButtonLoader />}
              {watchState ? `Toggle ${togglingOn ? "ON" : "OFF"}` : "Toggle"}
            </Button>
          </ToggleWrap>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalFacilityState.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const ModalHeading = styled(Heading)`
  display: flex;
  align-items: center;
  margin-bottom: ${pad}px;
`;

const ToggleWrap = styled.span`
  display: inline-block;
`;

const ActiveStateCard = styled.div`
  width: 100%;
  padding: ${pad}px ${pad * 1.2}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
`;

const Email = styled.div`
  line-height: ${pad * 2.5}px;
  margin-left: ${pad}px;

  abbr {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ApplyOtherTypes = styled.div`
  width: 100%;
  margin-bottom: ${pad}px;

  > div {
    margin-top: 0;
  }
`;

const OtherTypesPanel = styled.div`
  width: 100%;
  margin-top: ${pad / 2}px;
  padding-left: ${pad * 2.5}px;

  > div {
    margin-top: 0;
  }
`;

const StateCircle = styled.span`
  display: inline-grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  box-shadow: ${shadow};
  position: relative;
  isolation: isolate;
  flex-shrink: 0;
  background-color: ${({color, theme}) => color || theme.error};
  color: ${({theme}) => theme.tertiary};

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: ${({color, theme}) => color || theme.error};
    border-radius: 50%;
    z-index: -1;
    animation: ${({color, theme}) => pulse(color || theme.error)} 2s infinite;
  }
`;

const StateCircleChar = styled.span`
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  text-transform: uppercase;
  /* Font glyphs sit high in the em-box; nudge for optical center in a 16px badge */
  transform: translateY(0.5px);
`;

export default ModalFacilityState;
